import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import { getPackagingDisplayPayload } from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/Packaging/PackagingConfiguration/helpers'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

const usePackagingList = ({ storeId, setToastInfo }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const [accumulatedResults, setAccumulatedResults] = useState([])
  const [hasLocalChanges, setHasLocalChanges] = useState(false)
  const currentPageRef = useRef(1)
  const nameRef = useRef('')

  const {
    data: packagingData,
    loading: fetchingPackaging,
    fetchData: getPackagingData,
    resetData,
  } = useFetch(configurationServices.getPackaging, {
    doInitialRequest: !!storeId,
    params: { storeId, page: 1 },
  })

  useEffect(() => {
    if (packagingData?.results) {
      if (currentPageRef.current === 1) {
        setAccumulatedResults(packagingData.results)
      } else {
        setAccumulatedResults(prev => [...prev, ...packagingData.results])
      }
    }
  }, [packagingData])

  const { fetchData: editPackagingDisplay, loading: editingPackaging } = useFetch(
    configurationServices.editPackagingDisplay,
    { doInitialRequest: false }
  )

  const { fetchData: deletePackagingItem } = useFetch(configurationServices.deletePackaging, {
    doInitialRequest: false,
  })

  const { fetchData: setDefaultPackagingItem } = useFetch(configurationServices.setDefaultPackaging, {
    doInitialRequest: false,
  })

  const setPackaging = updater => {
    setAccumulatedResults(prev => updater(prev))
    setHasLocalChanges(true)
  }

  const getParams = page => {
    const params = { storeId, page }
    if (nameRef.current) params.search = nameRef.current
    return params
  }

  const loadMore = () => {
    const nextPage = currentPageRef.current + 1
    currentPageRef.current = nextPage
    getPackagingData(getParams(nextPage))
  }

  const refetchAll = () => {
    currentPageRef.current = 1
    setHasLocalChanges(false)
    getPackagingData(getParams(1))
  }

  const onSearch = query => {
    nameRef.current = query
    currentPageRef.current = 1
    setHasLocalChanges(false)
    getPackagingData(getParams(1))
  }

  const onSave = async () => {
    try {
      await editPackagingDisplay({
        storeId,
        packaging: getPackagingDisplayPayload(accumulatedResults),
      })
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
      setHasLocalChanges(false)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onDelete = async packagingId => {
    try {
      await deletePackagingItem({ storeId, packagingId })
      setToastInfo({ show: true, message: t('Embalaje eliminado'), type: 'success' })
      setAccumulatedResults(prev => prev.filter(item => item.id !== packagingId))
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const handleChangeEnabled = (id, checked) => {
    setAccumulatedResults(prev => prev.map(item => (item.id === id ? { ...item, is_enabled: checked } : item)))
    setHasLocalChanges(true)
  }

  const onEdit = packagingItem => {
    navigate(routes.configurationPage.logistics.packagingDetailConfiguration.replace(':id', packagingItem.id), {
      state: {
        childPageTitle: packagingItem?.name,
        selectedPackaging: packagingItem,
        previousLocation: location,
      },
    })
  }

  const onDuplicate = packagingItem => {
    navigate(routes.configurationPage.logistics.packagingDetailConfiguration.replace(':id', 'new'), {
      state: {
        selectedPackaging: { ...packagingItem, id: undefined, is_default: false },
        previousLocation: location,
      },
    })
  }

  const onSetDefault = async packagingId => {
    try {
      await setDefaultPackagingItem({ storeId, packagingId })
      setToastInfo({ show: true, message: t('Embalaje predeterminado actualizado'), type: 'success' })
      refetchAll()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onCreate = () => {
    navigate(routes.configurationPage.logistics.packagingDetailConfiguration.replace(':id', 'new'), {
      state: { previousLocation: location },
    })
  }

  return {
    packagingData,
    packaging: accumulatedResults,
    setPackaging,
    fetchingPackaging,
    handleChangeEnabled,
    onSave,
    editingPackaging,
    onDelete,
    onEdit,
    onDuplicate,
    onCreate,
    onSetDefault,
    revertChanges: () => {
      resetData()
      refetchAll()
    },
    hasChanges: hasLocalChanges,
    onSearch,
    loadMore,
    hasNextPage: !!packagingData?.next,
    totalCount: packagingData?.count || 0,
  }
}

export default usePackagingList
